import {
  AddLocationAlt,
  Close,
  CloseRounded,
  Restaurant,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItem,
  Paper,
  Popover,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import _, { capitalize, debounce } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Geocode from "react-geocode";
import TagManager from "react-gtm-module";
import MapContext from "../../Contexts/MapContext";
import UserContext from "../../Contexts/UserContext";
import { getIsPdp } from "../../Utils/pdpUtils";
import { sortOptions } from "../../Utils/SearchUtils";
import {
  calculateMaxMi,
  DEFAULT_MAP_ZOOM,
  getInitialCoordinates,
  sortByCoordinates,
  startCase,
} from "../../Utils/Utils";
import { useSearchLogic } from "../../Utils/searchHook";
import PlacesAutocomplete, {
  geocodeBySuggestion,
} from "../Components/PlacesAutocomplete";
import BuyOnlineButton from "./BuyOnlineButton";
import ProductFilterContent from "./ProductFilterContent";
import ResultList from "./ResultList";
import SuggestStoreForm from "./SuggestStoreForm";

Geocode.setApiKey(process.env.REACT_APP_GOOGLE_API_KEY);

export default function SearchPanel({
  addLayer,
  onFilterChange,
  fetchDelivery,
  region,
  setShowSearch,
}) {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const isPdp = getIsPdp();
  const theme = useTheme();

  const isMobile = useMediaQuery("(max-width: 768px)") && !isPdp;

  const userContext = useContext(UserContext);
  const mapContext = useContext(MapContext);
  const { results = [] } = mapContext;

  const renderCount = useRef(0);
  renderCount.current = renderCount.current + 1;

  const [selectedSort, setSelectedSort] = useState(
    userContext.state?.config?.defaultSort,
  );
  const [mapIsMoving, setIsMoving] = useState(false);
  const [term, setTerm] = useState(
    params.get("term") || params.get("location") || "",
  );
  const [suggestSuccess, setSuggestSuccess] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [filters, setFilters] = useState(() => {
    const filtersParam = params.get("filters")
      ? JSON.parse(params.get("filters"))
      : { chain: "", type: "", product: [], country: "" };

    const productParam = params.get("product");
    const chainParam = params.get("chain");
    const typeParam = params.get("type");
    const countryParam = params.get("country");

    return {
      ...filtersParam,
      product: productParam
        ? productParam.split(",").map((p) => (isNaN(p) ? p : Number(p)))
        : filtersParam.product,
      chain: chainParam || filtersParam.chain,
      type: typeParam || filtersParam.type,
      country: countryParam || filtersParam.country,
    };
  });
  const [isSuggestingStore, setIsSuggestingStore] = useState(false);
  const [listUpdates, setListUpdates] = useState(1);
  const [productFilterEl, setProductFilterEl] = React.useState(null);
  const isStrictProductSearch = useRef(false);
  const lastSearchedParams = useRef({
    coordinates: [],
    zoom: null,
    filters: null,
  });
  const isFirstLoad = useRef(true);

  const setResults = useCallback(
    (results) => {
      mapContext.setMapContext((oldState) => {
        const newResults =
          typeof results === "function" ? results(oldState.results) : results;
        return {
          ...oldState,
          results: newResults,
        };
      });
    },
    [mapContext.setMapContext],
  );

  const initialCoordinatesFromConfig = useMemo(() => {
    const coords = getInitialCoordinates(
      userContext.state?.config,
      userContext.state?.orgProperties,
      undefined,
      region,
    );
    return [coords.lat, coords.lng];
  }, [userContext.state?.config, userContext.state?.orgProperties, region]);

  const {
    loading,
    searchParams,
    setSearchParams,
    search,
    searchWithTerm,
  } = useSearchLogic({
    userContext,
    mapContext,
    filters,
    isStrictProductSearch,
    addLayer,
    fetchDelivery,
    setResults,
    setShowSearch,
    setIsSuggestingStore,
    params,
    initialCoordinates: initialCoordinatesFromConfig,
  });

  const productFilterOpen = Boolean(productFilterEl);

  const storeLabel =
    userContext.state?.orgProperties?.storeNameReplacement || "Store";
  const storeTypeLabel =
    userContext.state?.orgProperties?.storeTypeNameReplacement || "Type";
  const productLabel =
    userContext.state?.orgProperties?.productNameReplacement || "Product";
  const chainLabel =
    userContext.state?.orgProperties?.chainNameReplacement || "Retailer";

  const handleKeyDown = (event) => {
    setTerm(event.target.value || "");

    if (event.key === "Enter") {
      setSearchOpen(false);
      searchWithTerm(undefined, term);
    }
  };

  const onSuggestionSelected = (suggestion) => {
    setTerm(suggestion.description);
    geocodeBySuggestion(suggestion)
      .then((results) => {
        if (results.length < 1) {
          console.error("Couldn't find the location you entered");
          return;
        }

        const zipcodeIndex = _.findKey(results[0].address_components, {
          types: ["postal_code"],
        });
        const zipcode =
          zipcodeIndex > -1
            ? results[0].address_components[zipcodeIndex].long_name
            : "";

        const { geometry } = results[0];
        let geopoliticalRegion = "street_address";
        for (const type of [
          "street_address",
          "postal_code",
          "locality",
          "administrative_area_level_1",
          "country",
        ]) {
          if (suggestion.types.includes(type)) {
            geopoliticalRegion = type;
            break;
          }
        }

        setSearchParams((oldParams) => ({
          ...oldParams,
          coordinates: [geometry.location.lat(), geometry.location.lng()],
          _searchParams: {
            ...oldParams?._searchParams,
            searchedFirstTime:
              oldParams?._searchParams?.searchedFirstTime === undefined
                ? false
                : true,
            zipcode,
            geopoliticalRegion,
            term: suggestion.description,
            preventFly: false,
          },
        }));
      })
      .catch((err) => {
        console.error("SearchPanel.js:geocodeBySuggestion", err);
      });
  };

  const doReset = () => {
    setResults([]);
    mapContext.toggleModalPresent?.();
    setTerm("");
  };

  const showResultDetailsPage = (
    res,
    event,
    reviews,
    index,
    openInfo,
    filters,
  ) => {
    mapContext.toggleModalPresent?.(res, reviews, index, openInfo, filters);
  };

  const filterOptions = useMemo(() => {
    const noChainName =
      userContext.state?.orgProperties?.noChainNameReplacement ||
      "Independent Stores";
    const blacklistChainsFilter =
      userContext.state?.config?.blacklistChainsFilter || [];
    const getFilterList = {
      Product: (mapContext.products || []).reduce(
        (acc, p) => acc.set(p.id, startCase(_.toLower(p.name))),
        new Map(),
      ),
      Chain: (
        userContext.state?.config?.limitChainsFilter ||
        userContext.state?.properties?.find(
          (properties) => properties.name === "chain",
        )?.value ||
        []
      ).reduce((acc, p) => {
        if (!p || blacklistChainsFilter.includes(p)) {
          return acc;
        }
        const upperCasePattern = /\bh-?e-?-b\b/i;
        let formatMethod = capitalize;
        if (upperCasePattern.test(p || "")) {
          formatMethod = _.upperCase;
        }
        return noChainName === p
          ? acc.set("null", noChainName)
          : acc.set(p, _.toLower(p).replace(/\S+/g, formatMethod));
      }, new Map()),
      Type: (
        userContext.state?.properties?.find(
          (properties) => properties.name === "type",
        )?.value || []
      ).reduce((acc, p) => acc.set(p, startCase(_.toLower(p))), new Map()),
    };
    return (userContext.state?.config?.searchFilters || []).reduce(
      (acc, filterType) => ({
        ...acc,
        [filterType]: [
          ["", "All"],
          ...(filterType === "Chain" &&
          !blacklistChainsFilter.includes(noChainName) &&
          !userContext.state?.config?.limitChainsFilter?.includes(noChainName)
            ? [["null", noChainName]]
            : []),
          ...Array.from(getFilterList[filterType].entries())
            .map((entry) => [
              ...entry,
              _.filter(results, (res) =>
                filterType.toLowerCase() === "product"
                  ? res.products?.find((p) => p.product_id === entry[0])
                  : _.toLower(res[filterType.toLowerCase()]) ===
                    _.toLower(entry[0]),
              )?.length,
            ])
            .sort((a, b) =>
              userContext.state?.config?.limitChainsFilter
                ? 0
                : b[2] - a[2] || _.toLower(a).localeCompare(_.toLower(b)),
            ),
        ],
      }),
      {},
    );
  }, [mapContext.products, results, userContext.state]);

  const debouncedAddLayer = useMemo(
    () =>
      debounce((addLayer, mapContext, selectedSort, setResults) => {
        setResults((oldResults) => {
          const newResults = selectedSort
            ? sortOptions[selectedSort](oldResults)
            : sortByCoordinates(
                oldResults,
                mapContext.viewport.latitude,
                mapContext.viewport.longitude,
              );
          addLayer(newResults, false);
          return newResults;
        });
        setListUpdates((oldUpdate) => oldUpdate + 1);
      }, 500),
    [],
  );

  const configLoadedRef = useRef(false);
  useEffect(() => {
    if (userContext.state?.config && !configLoadedRef.current) {
      configLoadedRef.current = true;
      const initialCoordinates = getInitialCoordinates(
        userContext.state?.config,
        userContext.state?.orgProperties,
        params.get("zoom") ? Number(params.get("zoom")) : undefined,
        region,
      );
      mapContext.updateViewport?.({
        ...mapContext.viewport,
        latitude: initialCoordinates.lat,
        longitude: initialCoordinates.lng,
        zoom:
          searchParams.zoom ||
          userContext.state?.config?.initialZoom ||
          DEFAULT_MAP_ZOOM,
      });
    }
  }, [userContext.state?.config, region, mapContext.updateViewport]);

  useEffect(() => {
    if (!userContext.state?.config || isFirstLoad.current) {
      return;
    }
    const updateMapViewWithCoordinates = async (pos) => {
      if (pos.coords) {
        setSearchParams((oldParams) => {
          if (
            oldParams.coordinates?.[0] === pos.coords.latitude &&
            oldParams.coordinates?.[1] === pos.coords.longitude &&
            oldParams.zoom === mapContext.viewport.zoom
          ) {
            return oldParams;
          }
          return {
            ...oldParams,
            coordinates: [pos.coords.latitude, pos.coords.longitude],
            zoom: mapContext.viewport.zoom,
            _searchParams: {
              ...oldParams?._searchParams,
              searchedFirstTime:
                oldParams?._searchParams?.searchedFirstTime === undefined
                  ? false
                  : true,
              preventFly: true,
            },
          };
        });
      }
    };

    const isUsingDefaultUSPos =
      mapContext.viewport.latitude === 37.09024 &&
      mapContext.viewport.longitude === -95.712891;

    if (
      searchParams.coordinates &&
      window.turf?.point &&
      window.turf?.distance &&
      !isNaN(searchParams.coordinates[0]) &&
      !isNaN(searchParams.coordinates[1]) &&
      !isNaN(mapContext.viewport.latitude) &&
      !isNaN(mapContext.viewport.longitude) &&
      !isUsingDefaultUSPos
    ) {
      const from = window.turf.point(searchParams.coordinates);
      const to = window.turf.point([
        mapContext.viewport.latitude,
        mapContext.viewport.longitude,
      ]);
      const distance = window.turf.distance(from, to, { units: "miles" });
      const searchDistance = calculateMaxMi(
        searchParams.coordinates[0],
        searchParams.zoom,
      );

      const isZoomingOut =
        mapContext.isZooming &&
        searchParams.zoom &&
        searchParams.zoom - mapContext.viewport.zoom >= 1;
      const movingPastSearchDistance =
        distance > searchDistance && (term === "" || mapContext.isDragging);
      if (
        !mapContext.inTransition &&
        (isZoomingOut || movingPastSearchDistance)
      ) {
        updateMapViewWithCoordinates({
          coords: {
            latitude: mapContext.viewport.latitude,
            longitude: mapContext.viewport.longitude,
          },
        });
      }
    } else if (
      !searchParams.coordinates &&
      !isNaN(mapContext.viewport.latitude) &&
      !isNaN(mapContext.viewport.longitude) &&
      !isUsingDefaultUSPos
    ) {
      updateMapViewWithCoordinates({
        coords: {
          latitude: mapContext.viewport.latitude,
          longitude: mapContext.viewport.longitude,
        },
      });
    }

    if (
      !mapContext.inTransition &&
      !mapContext.isDragging &&
      !mapContext.isZooming &&
      !isFirstLoad.current
    ) {
      debouncedAddLayer(addLayer, mapContext, selectedSort, setResults);
    }
  }, [
    selectedSort,
    searchParams.coordinates,
    searchParams.zoom,
    mapContext.inTransition,
    mapContext.isDragging,
    mapContext.isZooming,
    mapContext.viewport.latitude,
    mapContext.viewport.longitude,
    mapContext.viewport.zoom,
    term,
    addLayer,
    setResults,
    debouncedAddLayer,
    setSearchParams,
  ]);

  useEffect(() => {
    const { coordinates, zoom, _searchParams } = searchParams;

    if (!userContext.state?.config) {
      return;
    }

    if (!coordinates && (params.get("term") || params.get("location"))) {
      searchWithTerm(params.get("term") || params.get("location"));
      return;
    }

    if (coordinates?.length === 2 && zoom) {
      if (params.get("zipcode")) {
        searchWithTerm(params.get("zipcode"));
        const url = new URL(window.location);
        url.searchParams.delete("coordinates");
        url.searchParams.delete("zipcode");
        window.history.pushState({}, "", "?" + url.searchParams.toString());
        return;
      }

      if (
        lastSearchedParams.current.coordinates[0] === coordinates[0] &&
        lastSearchedParams.current.coordinates[1] === coordinates[1] &&
        lastSearchedParams.current.zoom === zoom &&
        _.isEqual(lastSearchedParams.current.filters, filters)
      ) {
        return;
      }

      lastSearchedParams.current = {
        coordinates,
        zoom,
        filters: _.cloneDeep(filters),
      };
      search({ lat: coordinates[0], lng: coordinates[1] }, zoom, _searchParams);
      isFirstLoad.current = false;
    }
  }, [
    searchParams.coordinates,
    searchParams.zoom,
    searchParams._searchParams,
    search,
    searchWithTerm,
    params,
    filters,
  ]);

  useEffect(() => {
    if (!mapContext.deliveryInventory) {
      setTabIndex(0);
    }
  }, [mapContext.deliveryInventory]);

  useEffect(() => {
    if (mapContext.trackUserLocation && term !== "") {
      setTerm("");
    }
  }, [mapContext.trackUserLocation, term]);

  const getFilterTypeName = (filterType) => {
    switch ((filterType || "").toLowerCase()) {
      case "type":
        return storeTypeLabel;
      case "product":
        return productLabel;
      case "chain":
        return chainLabel;
      default:
        return filterType;
    }
  };

  const StoreResultsTabs = () => (
    <Stack direction={"row"}>
      <Button
        variant="text"
        sx={{
          color:
            tabIndex === 0
              ? theme.palette.secondary.main
              : theme.palette.primary.contrastText,
          flex: 1,
          borderBottom: tabIndex === 0 ? "2px solid" : "none",
          borderRadius: 0,
        }}
        onClick={() => setTabIndex(0)}
      >
        Buy in store
      </Button>
      <Button
        sx={{
          color:
            tabIndex === 1
              ? theme.palette.secondary.main
              : theme.palette.primary.contrastText,
          borderBottom: tabIndex === 1 ? "2px solid" : "none",
          borderRadius: 0,
          flex: 1,
        }}
        onClick={() => setTabIndex(1)}
        disabled={!mapContext.deliveryInventory}
        id="map-buy-online"
      >
        <>
          {userContext.state?.config?.buyOnlineButtonText || "Buy Online"}
          {!mapContext.deliveryInventory && (
            <CircularProgress
              style={{ position: "absolute", right: 0, top: 15 }}
              color="secondary"
              size={18}
            />
          )}
        </>
      </Button>
    </Stack>
  );

  useEffect(() => {
    const delayedFunction = debounce(() => {
      setIsMoving(false);
    }, 500);

    if (
      !mapContext.isZooming &&
      !mapContext.isDragging &&
      !mapContext.inTransition
    ) {
      delayedFunction();
    } else {
      setIsMoving(true);
    }

    return () => {
      delayedFunction.cancel();
    };
  }, [mapContext.isZooming, mapContext.isDragging, mapContext.inTransition]);

  const searchPanelStack = () => {
    const getTypeIcon = (type) => {
      switch (type) {
        case "store":
          return <ShoppingCartOutlined size={36} color="primary" />;
        case "popup_store":
          return <AddLocationAlt size={36} color="primary" />;
        default:
          return <Restaurant size={36} color="primary" />;
      }
    };
    const productFilter =
      filters.product && typeof filters.product === "string"
        ? [+filters.product]
        : filters.product || [];
    const productFilterValue = productFilter.length
      ? productFilter.length === 1
        ? mapContext.products?.find((p) => productFilter[0] === p.id)?.name
        : `${productFilter.length} selected`
      : "";

    const onSelectedProductsChange = (ids, strictSelection) => {
      isStrictProductSearch.current = !!strictSelection;
      setFilters((f) => ({ ...f, product: ids }));
      onFilterChange?.({
        ...filters,
        product: ids,
      });
      setProductFilterEl(null);
      ids.forEach((id) => {
        TagManager.dataLayer({
          dataLayer: {
            event: "flavorFilterChange",
            category: "Product Filter",
            label: id,
            data: mapContext.products.find((p) => id === p.id)?.name,
          },
        });
      });
    };

    return (
      <Stack direction="column" spacing="10px" height="100%">
        {!!mapIsMoving && <LinearProgress />}
        {!mapIsMoving && !isSuggestingStore && (
          <form id="search-panel" onSubmit={(e) => e.preventDefault()}>
            <Stack direction="column" spacing="10px">
              <Stack direction="row">
                <PlacesAutocomplete
                  open={searchOpen}
                  onOpen={() => setSearchOpen(true)}
                  onClose={() => setSearchOpen(false)}
                  textFieldProps={{
                    onKeyDown: (event) => handleKeyDown(event),
                    onChange: (e) => {
                      setTerm(e.target.value || "");
                    },
                    onFocus: () => {
                      setShowSearch(true);
                    },
                    fullWidth: true,
                    margin: "dense",
                    value: term,
                    placeholder: "Address / Zipcode",
                    type: "search",
                    name: "address",
                    inputProps: {
                      value: term,
                      style: {
                        fontSize: "16px",
                      },
                    },
                  }}
                  onSuggestionSelected={(e) => {
                    if (e) {
                      onSuggestionSelected(e);
                    } else {
                      doReset();
                    }
                  }}
                  createAutocompleteRequest={(inputValue) => {
                    const isSearchingAddress = /\d+.*[A-z]/.exec(inputValue);
                    return {
                      input: inputValue,
                      types: [isSearchingAddress ? "geocode" : "(regions)"],
                    };
                  }}
                />
                {(isMobile || isPdp) && <div style={{ width: 50 }}></div>}
              </Stack>
              <Stack direction="row" spacing="10px" width="100%">
                {userContext.state?.config?.searchFilters?.includes(
                  "Product",
                ) &&
                  !isPdp && (
                    <>
                      <TextField
                        label={getFilterTypeName("Product") + "s"}
                        variant="filled"
                        fullWidth
                        size="small"
                        onClick={(event) =>
                          setProductFilterEl(event.currentTarget)
                        }
                        value={productFilterValue}
                        focused={productFilterOpen}
                        InputProps={{
                          endAdornment:
                            productFilterValue === "" ? undefined : (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="clear product filter"
                                  edge="end"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onSelectedProductsChange([], false);
                                  }}
                                >
                                  <CloseRounded />
                                </IconButton>
                              </InputAdornment>
                            ),
                        }}
                      />
                      {productFilterOpen && (
                        <Popover
                          open={productFilterOpen}
                          anchorEl={productFilterEl}
                          onClose={() => setProductFilterEl(null)}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                          }}
                        >
                          <ProductFilterContent
                            selectedProducts={productFilter}
                            isStrictSelection={isStrictProductSearch.current}
                            setSelectedProducts={onSelectedProductsChange}
                          />
                        </Popover>
                      )}
                    </>
                  )}
                {userContext.state?.config?.searchFilters
                  ?.filter((f) => f !== "Product")
                  .map((filterType) => (
                    <Autocomplete
                      key={filterType}
                      sx={{ width: "100%" }}
                      options={filterOptions[filterType] || []}
                      value={
                        (filterOptions[filterType] || []).find(
                          (opt) =>
                            _.toLower(opt[0]) ===
                            _.toLower(filters[filterType.toLowerCase()]),
                        ) || (filterOptions[filterType] || [])[0]
                      }
                      getOptionLabel={(option) =>
                        `${option[1] || ""} ${
                          option[2] &&
                          userContext.state?.config?.showOptionCount
                            ? `(${option[2]})`
                            : ""
                        }`
                      }
                      onChange={(event, option) => {
                        const value = option?.[0] || "";
                        const label = option?.[1] || "";
                        setFilters({
                          ...filters,
                          [filterType.toLowerCase()]: value,
                        });
                        onFilterChange?.({
                          ...filters,
                          [filterType.toLowerCase()]: value,
                        });
                        TagManager.dataLayer({
                          dataLayer: {
                            event:
                              filterType === "Chain"
                                ? "chainFilterChange"
                                : filterType === "Product"
                                ? "flavorFilterChange"
                                : "typeFilterChange",
                            category: filterType + " Filter",
                            label: value,
                            data: label,
                            snowplowParameters: {
                              terms: [`${value}`, `${label}`],
                              filters: {
                                [filterType + "-Filter"]: `${value}`,
                              },
                            },
                          },
                        });
                      }}
                      autoComplete
                      includeInputInList
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={getFilterTypeName(filterType)}
                          variant="filled"
                          fullWidth
                          size="small"
                          name={`filter-${filterType}`}
                        />
                      )}
                      renderOption={(props, option, state, ownerState) => (
                        <ListItem {...props} dense sx={{ fontSize: 14 }}>
                          {ownerState.getOptionLabel
                            ? ownerState.getOptionLabel(option)
                            : option}
                        </ListItem>
                      )}
                    />
                  ))}
              </Stack>
            </Stack>
          </form>
        )}
        {!isSuggestingStore &&
          !mapIsMoving &&
          !!userContext.state?.config?.showBuyOnlineButton && (
            <StoreResultsTabs />
          )}
        {!isMobile && !isSuggestingStore && tabIndex === 0 && (
          <Stack direction="column" spacing={1} style={{ flex: 1 }}>
            {!!userContext.state?.config?.searchTypes?.length && (
              <Stack
                direction="row"
                width="100%"
                alignItems="flex-start"
                justifyContent="flex-start"
                spacing={1}
              >
                {["store", ...userContext.state.config.searchTypes].map(
                  (type) => (
                    <Stack
                      key={type}
                      direction="column"
                      spacing={0}
                      alignItems="center"
                      onClick={
                        _.toLower(filters.type || "").includes(_.toLower(type))
                          ? undefined
                          : () => {
                              setFilters((oldFilters) => ({
                                ...oldFilters,
                                type: [
                                  ...((oldFilters.type || "").split(",")[0]
                                    ? (oldFilters.type || "").split(",")
                                    : []),
                                  type,
                                ].join(","),
                              }));
                              onFilterChange?.({
                                ...filters,
                                type: [
                                  ...((filters.type || "").split(",")[0]
                                    ? (filters.type || "").split(",")
                                    : []),
                                  type,
                                ].join(","),
                              });
                            }
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <div
                        style={{
                          borderRadius: 200,
                          padding: 3,
                          border: `1px solid ${
                            _.toLower(filters.type || "").includes(
                              _.toLower(type),
                            )
                              ? theme.palette.primary.contrastText
                              : "transparent"
                          }`,
                          position: "relative",
                        }}
                      >
                        {_.toLower(filters.type || "").includes(
                          _.toLower(type),
                        ) && (
                          <IconButton
                            aria-label="remove type filter"
                            style={{
                              borderRadius: 200,
                              position: "absolute",
                              top: -5,
                              right: -5,
                              backgroundColor:
                                theme.palette.primary.contrastText,
                              padding: 2,
                            }}
                            size="small"
                            onClick={() => {
                              setFilters((oldFilters) => ({
                                ...oldFilters,
                                type: (oldFilters.type || "")
                                  .split(",")
                                  .filter((t) => t !== type)
                                  .join(","),
                              }));
                              onFilterChange?.({
                                ...filters,
                                type: (filters.type || "")
                                  .split(",")
                                  .filter((t) => t !== type)
                                  .join(","),
                              });
                            }}
                          >
                            <Close fontSize="inherit" />
                          </IconButton>
                        )}
                        <div
                          style={{
                            borderRadius: 200,
                            backgroundColor: theme.palette.secondary.main,
                            padding: 3,
                            height: "2em",
                            width: "2em",
                          }}
                        >
                          {getTypeIcon(type)}
                        </div>
                      </div>
                      <Typography
                        color={theme.palette.primary.contrastText}
                        style={{ fontSize: 10 }}
                      >
                        {startCase(type)}
                      </Typography>
                    </Stack>
                  ),
                )}
              </Stack>
            )}
            {loading && (
              <div
                style={{
                  justifyContent: "center",
                  alignItems: "center",
                  color: theme.palette.primary.contrastText,
                }}
              >
                <Stack>
                  <Typography>Searching for {storeLabel}...</Typography>
                  <LinearProgress />
                </Stack>
              </div>
            )}
            {!results?.length && !loading && (
              <div
                style={{
                  justifyContent: "center",
                  alignItems: "center",
                  color: theme.palette.primary.contrastText,
                }}
              >
                <Stack alignItems="center" style={{ textAlign: "center" }}>
                  <Typography>
                    No {storeLabel} in this area for your search
                  </Typography>
                  <Typography>
                    try changing search filters or location
                  </Typography>
                  <Typography>Suggest a {storeLabel} below.</Typography>
                </Stack>
              </div>
            )}
            {!!results?.length &&
              !loading &&
              !userContext.state.config?.hideAvailabilityMessage && (
                <Typography variant="subtitle2">
                  Always check availability with store
                </Typography>
              )}
            {!!results?.length &&
              (!loading || !!userContext.state.config?.useStoreStream) &&
              !mapIsMoving && (
                <div style={{ flex: 1 }}>
                  <ResultList
                    key={listUpdates}
                    data={
                      selectedSort
                        ? sortOptions[selectedSort](results)
                        : results
                    }
                    term={term}
                    showResultDetailsPage={showResultDetailsPage}
                    filters={filters}
                  />
                </div>
              )}
          </Stack>
        )}
        {!isMobile &&
          !isSuggestingStore &&
          tabIndex === 1 &&
          userContext.state?.config?.showBuyOnlineButton && (
            <BuyOnlineButton products={mapContext.products} />
          )}
        {userContext.state?.config?.allowSuggestions && (
          <Stack>
            {!isSuggestingStore && !mapIsMoving && (
              <Button
                variant="text"
                color="inherit"
                {...(userContext.state?.config?.suggestBtnProps || {})}
                onClick={() =>
                  userContext.state?.config?.suggestForm
                    ? window.open(
                        userContext.state.config.suggestForm,
                        "_blank",
                      )
                    : setIsSuggestingStore(true)
                }
              >
                Suggest {storeLabel}
              </Button>
            )}
            {isSuggestingStore && (
              <>
                <Stack spacing={2} p={1}>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Where would you like to find us next?
                    </Typography>
                    <Typography variant="caption">
                      Tell us which store you’d love to buy our products at!
                    </Typography>
                  </Stack>
                  <SuggestStoreForm
                    onSuggest={() => {
                      setSuggestSuccess(true);
                      setIsSuggestingStore(false);
                    }}
                    onCancel={() => setIsSuggestingStore(false)}
                    showResultDetailsPage={showResultDetailsPage}
                  />
                </Stack>
              </>
            )}
            <Snackbar
              open={suggestSuccess}
              autoHideDuration={6000}
              onClose={() => setSuggestSuccess(false)}
            >
              <Alert
                onClose={() => setSuggestSuccess(false)}
                severity="success"
                sx={{ width: "100%" }}
              >
                Store Suggested
              </Alert>
            </Snackbar>
          </Stack>
        )}
      </Stack>
    );
  };

  return (
    <>
      <Paper
        key={window.innerHeight}
        elevation={0}
        style={{
          margin: isPdp ? "0px" : "35px 26px",
          padding: 10,
          background: theme.palette.primary.main,
          width: isMobile || isPdp ? null : 300,
          height: isMobile ? undefined : isPdp ? "100vh" : "80vh",
          position: "absolute",
          left: 0,
          right: isMobile || isPdp ? 0 : null,
          zIndex: 1,
          color: theme.palette.primary.contrastText,
          borderRadius: isPdp ? 0 : undefined,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {!isMobile && searchPanelStack()}
        {!!isMobile && (
          <>
            <Collapse
              in={mapContext.showSearch}
              collapsedSize={mapIsMoving ? 30 : 50}
              timeout="auto"
            >
              {searchPanelStack()}
            </Collapse>
            {!mapContext.showSearch &&
              !mapIsMoving &&
              !!userContext.state?.config?.showBuyOnlineButton && (
                <StoreResultsTabs />
              )}
          </>
        )}
        {!!isMobile && loading && (
          <div
            style={{
              justifyContent: "center",
              alignItems: "center",
              color: theme.palette.primary.contrastText,
            }}
          >
            <Stack>
              <Typography>Searching for {storeLabel}...</Typography>
              <LinearProgress />
            </Stack>
          </div>
        )}
      </Paper>
      {results && isMobile && !isSuggestingStore && (
        <div
          style={{
            display: mapContext.highlight_result?.data ? "none" : "",
            width: "100%",
            position: "absolute",
            bottom: 0,
            zIndex: 1,
          }}
        >
          {tabIndex === 0 && !!results?.length && !mapIsMoving && (
            <ResultList
              key={listUpdates}
              data={results}
              showResultDetailsPage={showResultDetailsPage}
              filters={filters}
            />
          )}
          {tabIndex === 1 && userContext.state?.config?.showBuyOnlineButton && (
            <BuyOnlineButton products={mapContext.products} />
          )}
          {!loading && tabIndex === 0 && results && results?.length === 0 && (
            <div
              style={{
                position: "relative",
                zIndex: 1000,
                backgroundColor: "white",
                overflowY: "hidden",
                width: "100%",
                height: 160,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                padding: 10,
              }}
            >
              <div>
                No {storeLabel} in this area for your search, try changing
                search filters or location
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
